
import { ShoninshaModel } from "@/interfaces/account.interface";
import {
  defineComponent,
  inject,
  PropType,
  reactive,
  ref,
  Ref,
  watchEffect,
} from "vue";
import { getAllKengen } from "@/api/masterTable.api";
import { DialogGenre, RowId } from "@/interfaces/common.interface";
import {
  addShoninsha,
  checkAccount,
  checkShoninsha,
  getShoninshaById,
  updateShoninsha,
} from "@/api/account.api";
import { ElMessage } from "element-plus";
import { CheckMsg, InfoMsg } from "@/constants/message.constant";
type ValidateFieldCallback = (message?: string | Error) => void;

export default defineComponent({
  props: {
    title: String as PropType<string>, //ダイアログタイトル
    operation: String as PropType<DialogGenre>, //ダイアログ機能（登録/修正）
  },
  emits: ["search"],
  async setup(props, { emit }) {
    //親からの変数
    const dialogFormVisible = inject("dialogFormVisible") as Ref<boolean>;
    const rowId = inject("rowId") as Ref<RowId>;

    const form = reactive({
      accountId: "",
      tantoCd: "",
      tantoNm: "",
      email: "",
      kengenCd: "",
      tsuchiFlg: false,
    } as ShoninshaModel);
    const formRef = ref();
    const loading = ref(false);
    //入力情報のチェックルール
    const rules = {
      tantoCd: [
        {
          required: true,
          validator: async (
            self: any,
            value: string,
            callback: ValidateFieldCallback
          ) => {
            if (!value) {
              callback(CheckMsg.CHK_TANTO_CD);
            } else {
              try {
                await checkShoninsha({
                  id: rowId.value,
                  databaseField: "tanto_cd",
                  fieldName: self.name,
                  fieldValue: value,
                });
                callback();
              } catch (error) {
                callback(error);
              }
            }
          },
          trigger: "blur",
          name: "担当者コード",
        },
      ],
      tantoNm: [
        {
          required: true,
          message: CheckMsg.CHK_TANTO_NM,
          trigger: "blur",
        },
      ],
      email: [
        {
          type: "email",
          trigger: "blur",
          message: CheckMsg.CHK_MAIL2,
        },
        {
          required: true,
          validator: async (
            self: any,
            value: string,
            callback: ValidateFieldCallback
          ) => {
            if (!value) {
              callback(CheckMsg.CHK_MAIL);
            } else {
              try {
                await checkAccount({
                  id: rowId.value,
                  databaseField: "mail_address",
                  fieldName: self.name,
                  fieldValue: value,
                });
                callback();
              } catch (error) {
                callback(error);
              }
            }
          },
          trigger: "blur",
          name: "メールアドレス",
        },
      ],
      kengenCd: [
        {
          required: true,
          message: CheckMsg.CHK_KENGEN,
          trigger: "blur",
        },
      ],
    };

    //フォームリセット
    const clearForm = () => {
      formRef.value.resetFields();
      //resetFieldsメソッドはselectに効かないので、selectのみ手動で値を消す
      form.kengenCd = "";
      dialogFormVisible.value = false;
      rowId.value = undefined;
    };

    //登録/修正ボタンイベント
    const handleCreateOrUpdate = () => {
      formRef.value?.validate(async (valid: unknown) => {
        if (valid) {
          loading.value = true;
          try {
            const data = {
              tantoCd: form.tantoCd,
              tantoNm: form.tantoNm,
              email: form.email,
              kengenCd: form.kengenCd,
              tsuchiFlg: form.tsuchiFlg,
            };
            if (props.operation === DialogGenre.Add) {
              await addShoninsha(data);
              ElMessage({
                type: "success",
                message: InfoMsg.INFO_SYONINSHA_ADDED,
              });
            } else {
              await updateShoninsha(form.accountId, data);

              ElMessage({
                type: "success",
                message: InfoMsg.INFO_SYONINSHA_UPDATED,
              });
            }
            loading.value = false;
            clearForm();
            //再度検索
            emit("search");
          } catch (error) {
            loading.value = false;
          }
        } else {
          return false;
        }
      });
    };

    //修正モード時、選択行の情報取得
    const getShoninshaInfo = async (id: string) => {
      const {
        data: { result },
      } = await getShoninshaById(id);
      form.accountId = result.account.akaunto_id;
      form.tantoCd = result.tanto_cd;
      form.tantoNm = result.tanto_nm;
      form.email = result.account.mail_address;
      form.kengenCd = result.account.kengen.kengen_kubun_cd;
      form.tsuchiFlg = result.shinsei_tsuchi_flg;
    };

    watchEffect(async () => {
      if (props.operation === DialogGenre.Edit && rowId.value) {
        await getShoninshaInfo(rowId.value as string);
      }
    });

    //権限selectボックス初期化
    const kengenOptions = await getAllKengen();

    return {
      dialogFormVisible,
      form,
      formRef,
      loading,
      rules,
      kengenOptions,
      DialogGenre,
      handleCreateOrUpdate,
      clearForm,
    };
  },
});
